import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { intakeApi } from '../api/services';
import toast from 'react-hot-toast';
import { Upload, Loader2, FileText, CheckCircle, AlertTriangle } from 'lucide-react';

export default function IntakeUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { toast.error('Pick a CSV file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await intakeApi.upload(form);
      setResult(res.data);
      toast.success(`Created intake batch with ${res.data.ordersCreated?.length || 0} order(s)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Upload MBO CSV</h1>
      <p className="text-sm text-gray-500 mb-5">
        Orders are created from the file and routed automatically by type.
        Virtual products go to Ops, reships go to the unlinked queue, troublesome customers are blocked.
      </p>

      <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
        <strong>Filename format required:</strong>{' '}
        <code className="font-mono text-amber-900">IDM-DD-MM-YYYY-Order-N.csv</code>
        <br />
        <span className="text-xs">Example: <code>IDM-23-04-2026-Order-80.csv</code> — 80 orders dated 23 Apr 2026.
        Uploads with any other name will be rejected.</span>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5">
        <label className="block mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-1">CSV file</span>
          <input
            type="file" accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {file && (() => {
            const valid = /^IDM-\d{2}-\d{2}-\d{4}-Order-\d+\.csv$/i.test(file.name);
            return (
              <div className={`mt-2 flex items-center gap-2 text-xs ${valid ? 'text-gray-600' : 'text-red-700'}`}>
                <FileText className="w-3.5 h-3.5" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                {!valid && <span className="font-semibold">— filename will be rejected, please rename to match the format above.</span>}
              </div>
            );
          })()}
        </label>
        <button
          type="submit" disabled={!file || uploading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      {result && (
        <div className="mt-5 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="font-semibold">Upload complete</h2>
          </div>

          {/* Round 5: Upload Summary Popup section. When the parser flags
              any orders, surface counts + a fast-path to the validation
              dashboard. Clean orders are already moving through the
              pipeline; flagged ones sit in validation_pending until
              admin clears them. */}
          {result.validationFlaggedCount > 0 && (
            <div className="mb-4 bg-orange-50 border border-orange-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <span className="font-semibold text-orange-900">
                  {result.validationFlaggedCount} order(s) flagged for review
                </span>
              </div>
              <ul className="text-sm text-orange-900 ml-6 list-disc mb-3">
                {result.validationCaseCounts?.case_a > 0 && (
                  <li>{result.validationCaseCounts.case_a} new Indian Brand(s) — Case A</li>
                )}
                {result.validationCaseCounts?.case_b > 0 && (
                  <li>{result.validationCaseCounts.case_b} brand+generic both new — Case B</li>
                )}
                {result.validationCaseCounts?.case_c > 0 && (
                  <li>{result.validationCaseCounts.case_c} container mismatch(es) — Case C</li>
                )}
                {result.validationCaseCounts?.shipper > 0 && (
                  <li>{result.validationCaseCounts.shipper} shipper-rule miss(es)</li>
                )}
              </ul>
              <p className="text-xs text-orange-800 mb-3">
                Clean orders ({(result.ordersCreated?.length || 0) - result.validationFlaggedCount}) are already moving
                through the pipeline. Flagged orders are parked in <strong>Validation pending</strong>
                until you Approve / Reject / Skip each flag.
              </p>
              <div className="flex gap-2">
                <button onClick={() => navigate('/admin/validation-dashboard')}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700">
                  Review mapping
                </button>
                <button onClick={() => navigate(`/intake/${result.batch.id}`)}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  Open batch
                </button>
              </div>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-2 text-sm mb-3">
            <dt className="text-gray-500">Batch number</dt>
            <dd className="font-mono">{result.batch?.batch_number}</dd>
            <dt className="text-gray-500">Orders created</dt>
            <dd>{result.ordersCreated?.length || 0}</dd>
            <dt className="text-gray-500">Validation flagged</dt>
            <dd>{result.validationFlaggedCount || 0}</dd>
            <dt className="text-gray-500">Vendor auto-assigned</dt>
            <dd>{result.vendorAutoAssignedCount || 0}</dd>
            <dt className="text-gray-500">Duplicates skipped</dt>
            <dd>{result.ordersSkipped?.length || 0}</dd>
            <dt className="text-gray-500">Malformed rows</dt>
            <dd>{result.failedRows?.length || 0}</dd>
          </dl>
          {result.ordersSkipped?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-1">Skipped</div>
              <ul className="text-xs text-gray-600 list-disc list-inside">
                {result.ordersSkipped.map((s, i) => (
                  <li key={i}>{s.order_id_external}: {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.failedRows?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs font-semibold uppercase text-gray-500 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-600" /> Failed rows
              </div>
              <ul className="text-xs text-gray-600 list-disc list-inside">
                {result.failedRows.map((r, i) => (
                  <li key={i}>row {r.row_num}: {r.error}</li>
                ))}
              </ul>
            </div>
          )}
          <button
            onClick={() => navigate(`/intake/${result.batch.id}`)}
            className="mt-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            Open batch
          </button>
        </div>
      )}
    </div>
  );
}
